import React, { Component, Fragment } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VirtualPack } from '~/Api/Analytics/VirtualBrand';
import { GROUP_TYPE, ModuleType, PRODUCT_LIST_TYPE, UserProductGroup } from '~/Api/User/ProductGroup';
import config from '~/config';
import isUserProductGroupNameValid from '~/utils/UserProductGroup/isUserProductGroupNameValid';
import ProductGroupFolderSelect from '~/ProductGroupFolderSelect';
import Button, { Link } from '~/UI/Button';
import primaryAltSmallButtonStyles from '~/UI/Button/primary-alt-small.less';
import secondarySmallButtonStyles from '~/UI/Button/secondary-small.less';
import Checkbox from '~/UI/Checkbox/Checkbox';
import FormFieldError from '~/UI/FormFieldError/FormFieldError';
import Heading from '~/UI/Heading/Heading';
import InfoText, { Type as InfoTextType } from '~/UI/InfoText/InfoText';
import Input from '~/UI/Input/Input';
import inputWideStyles from '~/UI/Input/wide.less';
import ModalBody from '~/UI/Modal/Body/ModalBody';
import ModalContent from '~/UI/Modal/Content/ModalContent';
import ModalFooter from '~/UI/Modal/Footer/ModalFooter';
import ModalHeader from '~/UI/Modal/Header/ModalHeader';
import Modal from '~/UI/Modal/Modal';
import modalSmallStyles from '~/UI/Modal/small.less';
import Radio from '~/UI/Radio/Radio';
import { SelectedOption } from '~/UI/SegmentedSelect/SegmentedSelect';

import modalStyles from './modalStyles.less';
import styles from './styles.less';
import compactWiderStyles from './wideModalStyles.less';

export interface EnabledModule {
	readonly name: string;
	readonly type: ModuleType;
}

interface Props {
	title: React.ReactNode;
	namePlaceholder: string;
	groupType: string | null;
	initialInputValue: string;
	initialModule: ModuleType | null;
	saveButtonLabel: string | null;
	isFetching: boolean;
	enabledModules: EnabledModule[] | null;
	templateGroup?: UserProductGroup | null;
	folderSelection: boolean;
	isCzReimbursmentPrefillAllowed: boolean;
	virtualPack?: VirtualPack;
	onClose(): void;
	onCreateFromVirtualPack?(
		name: string,
		parentGroup: UserProductGroup | null,
		virtualPackId: string,
		czReimbursementLists: Array<{ codes: string[] }> | null
	): Promise<{ moduleError: string | null }>;
	onSave(
		name: string,
		module: ModuleType | null,
		parentGroup: UserProductGroup | null,
		czReimbursementLists: Array<{ codes: string[] }> | null
	): Promise<{ moduleError: string | null }>;
	onFetch?(): void;
}

interface State {
	inputValue: string;
	valueError: string | null;
	module: ModuleType | null;
	moduleError: string | null;
	selectedGroupOption: SelectedOption | null;
	groupNameFilter: string;
	createFromSuklCodes: boolean;
}

export default class CreateModal extends Component<Props, State> {
	static defaultProps = {
		groupType: null,
		initialInputValue: '',
		initialModule: null,
		saveButtonLabel: 'Save',
		isFetching: false,
		enabledModules: null,
		folderSelection: false,
	};

	inputElement = React.createRef<Input>();

	bodyElement = React.createRef<HTMLDivElement>();

	iframeElement = React.createRef<HTMLIFrameElement>();

	constructor(props: Props) {
		super(props);
		this.state = {
			inputValue: props.initialInputValue,
			valueError: null,
			module: props.initialModule,
			moduleError: null,
			selectedGroupOption: null,
			groupNameFilter: '',
			createFromSuklCodes: true,
		};
	}

	componentDidMount() {
		const { onFetch } = this.props;

		if (onFetch) {
			onFetch();
		}
		if (this.inputElement) {
			this.inputElement.current?.focus();
		}
	}

	render() {
		const {
			props: {
				title,
				namePlaceholder,
				saveButtonLabel,
				groupType,
				isFetching,
				templateGroup,
				folderSelection,
				isCzReimbursmentPrefillAllowed,
				onClose,
			},
			state: { module, inputValue, valueError, selectedGroupOption, createFromSuklCodes },
		} = this;
		const isFormValid =
			inputValue.trim() !== '' &&
			(!templateGroup || templateGroup.isGroup() || (templateGroup.isProductList() && selectedGroupOption));
		const showFolderSelect = folderSelection || (templateGroup && templateGroup.isProductList());
		const showSuklCodes = module === ModuleType.czReimbursement && templateGroup === undefined;

		return (
			<Modal
				styles={
					showFolderSelect ? modalStyles : showSuklCodes && createFromSuklCodes ? compactWiderStyles : modalSmallStyles
				}
				onClose={onClose}
			>
				<form data-testid='group-create-form' style={{ height: '100%' }} onSubmit={this.handleFormSubmit}>
					<ModalContent>
						<ModalHeader>
							<Heading level={2}>{title}</Heading>
							{groupType === PRODUCT_LIST_TYPE ? (
								<p>
									<strong>Suggest naming convention:</strong> Brand name, dosage strength, form, package size
									<br />
									<strong>Example:</strong> Simvastatin Xpharma 20 mg tbl pckg 30
								</p>
							) : null}
						</ModalHeader>
						<ModalBody contentRef={this.bodyElement}>
							<div className={showSuklCodes && createFromSuklCodes ? styles.firstRow : styles.content}>
								{templateGroup && templateGroup.isProductList() ? <Heading level={3}>List name</Heading> : null}
								<Input
									disabled={isFetching}
									isInvalid={valueError !== null}
									name='group-name'
									placeholder={namePlaceholder}
									ref={this.inputElement}
									styles={inputWideStyles}
									type='text'
									value={inputValue}
									onChange={(e) => this.setState({ inputValue: e.currentTarget.value })}
								/>
								{valueError !== null ? <FormFieldError>{valueError}</FormFieldError> : null}

								{groupType === GROUP_TYPE ? this.renderModule() : null}
								{showFolderSelect
									? this.renderFolderSelect()
									: templateGroup
									? this.renderWarning(templateGroup)
									: null}
							</div>
							{showSuklCodes && isCzReimbursmentPrefillAllowed ? (
								<div className={styles.secondRow}>{this.renderPreFill()}</div>
							) : null}
						</ModalBody>
						<ModalFooter>
							<Link styles={secondarySmallButtonStyles} onClick={onClose}>
								Cancel
							</Link>
							<div className={styles.button}>
								<Button disabled={!isFormValid || isFetching} styles={primaryAltSmallButtonStyles} onClick={() => null}>
									{saveButtonLabel}
								</Button>
							</div>
						</ModalFooter>
					</ModalContent>
				</form>
			</Modal>
		);
	}

	renderModule() {
		const {
			props: { isFetching },
			state: { module, moduleError },
			props: { enabledModules, isCzReimbursmentPrefillAllowed },
		} = this;

		return (
			<Fragment>
				<div className={styles.radioSet}>
					<div className={styles.radioSetItem}>
						<Radio
							checked={module === null}
							id='group-module-none'
							name='module'
							onChange={() => this.setState({ module: null })}
						>
							No module
						</Radio>
					</div>

					{(enabledModules || []).map((enabledModule) => (
						<div className={styles.radioSetItem} key={enabledModule.type}>
							<Radio
								disabled={isFetching}
								checked={module === enabledModule.type}
								id={`group-module-${enabledModule.type}`}
								name='module'
								onChange={() =>
									this.setState({
										module: enabledModule.type,
										createFromSuklCodes: isCzReimbursmentPrefillAllowed,
									})
								}
							>
								{enabledModule.name}
							</Radio>
						</div>
					))}
				</div>

				{moduleError !== null ? <FormFieldError>{moduleError}</FormFieldError> : null}
			</Fragment>
		);
	}

	renderFolderSelect() {
		const {
			props: { templateGroup },
			state: { selectedGroupOption, groupNameFilter },
		} = this;

		return (
			<Fragment>
				<Heading level={3}>Select folder</Heading>
				<p className={styles.paragraph}>Choose folder to which you want to copy list</p>

				<Input
					placeholder='Filter target folder…'
					styles={inputWideStyles}
					type='text'
					value={groupNameFilter}
					onChange={(e) => this.setState({ groupNameFilter: e.currentTarget.value })}
				/>

				<AutoSizer>
					{({ width, height }: { width: number; height: number }) => (
						<div className={styles.select} style={{ width, height: height - 220, paddingRight: width / 2 }}>
							<ProductGroupFolderSelect
								nameFilter={groupNameFilter}
								selectedOption={selectedGroupOption}
								onSelect={this.handleFolderSelect}
							/>
							{templateGroup ? this.renderWarning(templateGroup) : null}
						</div>
					)}
				</AutoSizer>
			</Fragment>
		);
	}

	handleFolderSelect = (option: SelectedOption | null) => {
		this.setState({ selectedGroupOption: option });
		if (this.bodyElement.current) {
			this.bodyElement.current.scrollTop = 0;
		}
	};

	renderWarning(templateGroup: UserProductGroup) {
		const {
			props: { groupType },
			state: { selectedGroupOption, module },
		} = this;

		if (selectedGroupOption && !templateGroup.hasModuleType(selectedGroupOption.value.moduleType)) {
			return (
				<div className={styles.error}>
					<InfoText header='Warning!' type={InfoTextType.danger}>
						Target folder has different module than the one which you are making copy of.
					</InfoText>
				</div>
			);
		}

		if (groupType === GROUP_TYPE && templateGroup.moduleType !== module) {
			return (
				<InfoText header='Warning!' type={InfoTextType.danger}>
					Selected module differs to the group which you are making copy of.
				</InfoText>
			);
		}

		return null;
	}

	renderPreFill() {
		const { isFetching } = this.props;
		const { createFromSuklCodes } = this.state;

		return (
			<>
				{isFetching ? <div className={styles.iframeOverlay} /> : null}
				<Checkbox
					disabled={isFetching}
					checked={createFromSuklCodes}
					onChange={(createFromSuklCodes) => {
						this.setState({ createFromSuklCodes });
					}}
				>
					Prefill products
					<p className={styles.warning}>
						Prefill function automatically finds products across reference countries for original, orphan and biosimilar
						medicines authorized by EMA. For other products, please use Search function to complete the product lists.
					</p>
				</Checkbox>

				{createFromSuklCodes ? (
					<iframe className={styles.iframe} ref={this.iframeElement} src={config.userProductGroup.suklCodesSourceUrl} />
				) : null}
			</>
		);
	}

	handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		const {
			props: { templateGroup, virtualPack, onCreateFromVirtualPack, onSave },
			state: { inputValue, module, selectedGroupOption, createFromSuklCodes },
		} = this;
		const nextState = { valueError: null, moduleError: null };

		event.preventDefault();

		const trimmedValue = inputValue.trim();
		if (!isUserProductGroupNameValid(trimmedValue)) {
			this.setState({
				...nextState,
				valueError: 'Please make the name longer, it must be at least 3 characters long',
			});
			return;
		}

		let suklCodes: Array<{ codes: string[] }> | null = null;
		if (module === ModuleType.czReimbursement && createFromSuklCodes && templateGroup === undefined) {
			try {
				suklCodes = await this.fetchSuklCodes();
			} catch {}

			if (suklCodes === null || suklCodes.length === 0) {
				this.setState({
					...nextState,
					moduleError: 'Please make sure that you selected group of products and try again',
				});
				return;
			}
		}

		this.setState(nextState);
		if (virtualPack && virtualPack.real && onCreateFromVirtualPack) {
			onCreateFromVirtualPack(
				trimmedValue,
				selectedGroupOption ? selectedGroupOption.value : null,
				virtualPack.id,
				suklCodes
			);
		} else {
			const result = await onSave(
				trimmedValue,
				module,
				selectedGroupOption ? selectedGroupOption.value : null,
				suklCodes
			);

			if (result.moduleError) {
				this.setState({
					...nextState,
					module: null,
					moduleError: result.moduleError,
				});
			}
		}
	};

	async fetchSuklCodes(): Promise<Array<{ codes: string[] }>> {
		return new Promise((resolve, reject) => {
			const timeout = setTimeout(() => {
				// eslint-disable-next-line no-use-before-define
				window.removeEventListener('message', listener);
				reject(new Error());
			}, 5000);

			const listener = (event: MessageEvent) => {
				if (event.data === null || typeof event.data !== 'object' || event.data.cogvioMessage !== 'ReturnSuklCodes') {
					return;
				}
				clearTimeout(timeout);
				window.removeEventListener('message', listener);
				const suklData = event.data.data as Array<{
					dis13DddQshare: string;
					dis13OdtdQshare: string;
					dis13PackagesQshare: string;
					dnDou: string;
					suklCode: string;
					suklCodes: string;
				}>;
				const codes = suklData
					.filter(
						(product) =>
							product.dnDou !== '' ||
							Number(product.dis13DddQshare) >= 3 ||
							Number(product.dis13OdtdQshare) >= 3 ||
							Number(product.dis13PackagesQshare) >= 3
					)
					.map((product) => ({
						codes: [product.suklCode, ...product.suklCodes.split(',')].filter((code) => code !== ''),
					}))
					.filter(({ codes }) => codes.length > 0);
				resolve(codes);
			};

			window.addEventListener('message', listener, false);
			this.iframeElement?.current?.contentWindow?.postMessage({ cogvioMessage: 'FetchSuklCodes' }, '*');
		});
	}
}
